// src/constants/messageTones.js
//
// Message tone presets for the AI Message Composer. Coaches pick a
// tone from their profile that gets injected into the AI system
// prompt — same generated message turns warmer / sharper / more
// playful depending on their choice. Without this the model defaults
// to "warm but professional" for everyone, which makes the marketing
// claim "drafts in your voice" technically untrue.
//
// Each entry has:
//   - id            : stable string used as the userProfile field value
//   - label         : human-readable label for the picker UI
//   - description   : 1-line subtitle shown in the picker
//   - example       : tiny preview snippet in the coach's chosen voice
//   - promptInjection: text appended to the system prompt before sending
//                     to Claude. Phrased as a directive ("Use a {style}
//                     voice. {What that means in practice}.").
//
// The first entry's `id` is treated as the default for users who
// haven't picked one yet.

export const DEFAULT_MESSAGE_TONE_ID = 'warm';

export const MESSAGE_TONES = [
  {
    id: 'warm',
    label: 'Warm & friendly',
    description: 'The default — approachable, personal, "Hi families."',
    example: '"Hi families! Quick reminder about practice tonight…"',
    promptInjection:
      'Use a warm but professional tone. Address parents as "families" or "team families". Be friendly and personal without being casual.',
  },
  {
    id: 'direct',
    label: 'Direct & efficient',
    description: 'No fluff. Clear facts, short sentences.',
    example: '"Practice tonight, 6 PM, Field 3. Bring water."',
    promptInjection:
      'Use a direct, efficient tone. Get straight to the point with no greeting or filler. Short sentences. Just the facts the parent needs.',
  },
  {
    id: 'playful',
    label: 'Playful & energetic',
    description: 'Light, fun, occasional emoji-free humor.',
    example: '"Hey team! See you tonight — bring your A game."',
    promptInjection:
      'Use a playful, energetic tone. Light humor is welcome. Address parents and players together. Stay enthusiastic but professional. Avoid emoji (SMS budget).',
  },
  {
    id: 'formal',
    label: 'Formal',
    description: 'School-admin style. Respectful, structured.',
    example: '"Dear families, please be advised practice is at 6:00 PM…"',
    promptInjection:
      'Use a formal, respectful tone. Address parents as "Dear families". Use complete sentences and proper structure. Avoid contractions where natural.',
  },
  {
    id: 'motivational',
    label: 'Motivational',
    description: 'High-energy, captain-style hype.',
    example: '"Big night ahead. Be on the field at 6, eyes up, ready to work."',
    promptInjection:
      'Use a motivational, captain-style tone. Energy and intensity. Speak to the team like a leader rallying them. Brief, punchy sentences. Earn-it framing over feel-good framing.',
  },
];

export function getMessageTone(id) {
  return (
    MESSAGE_TONES.find((t) => t.id === id) ||
    MESSAGE_TONES.find((t) => t.id === DEFAULT_MESSAGE_TONE_ID)
  );
}
